import '@/styles/designtoken.css';
import '@/styles/globals.css';

import { StyleProvider } from '@ant-design/cssinjs';
import { ConfigProvider } from 'antd';
import type { NextPage } from 'next';
import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import { useRouter } from 'next/router';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import React from 'react';
import { IntlProvider } from 'react-intl';

import ErrorBoundary from '@/components/common/ErrorBoundary';
import PageHead from '@/components/common/PageHead';
import PlufginDector from '@/components/common/PluginDector';
import { RightProvider } from '@/components/RightProvider';
import { WithContext } from '@/components/WithContext';
import { GlobalAudioPlayerProvider } from '@/contexts/GlobalAudioPlayerContext';
import { useInterCom } from '@/hooks/useIntercom';
import useTheme from '@/hooks/useTheme';
import en from '@/lang/en.json';
import zh from '@/lang/zh.json';
import useLoginStore from '@/store/Login';
import UserStore from '@/store/User';
import theme from '@/theme/index';
import { getCookie } from '@/utils/cookie';

/**
 * 性能优化：延迟初始化非关键 SDK
 * 使用 requestIdleCallback 在浏览器空闲时初始化，避免阻塞首屏渲染和交互
 * 参考：Vercel React Best Practices - bundle-defer-third-party
 */
const deferredInit = (callback: () => void, timeout = 3000) => {
  if (typeof window === 'undefined') return;

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout });
  } else {
    // Safari 不支持 requestIdleCallback，使用 setTimeout 作为 fallback
    setTimeout(callback, 100);
  }
};

const buildTime = process.env.BUILD_TIME;
const branchName = process.env.BRANCH_NAME;
const shortCommit = process.env.SHORT_COMMIT;
const appArea = process.env.APP_AREA;
const appEnv = process.env.APP_ENV;

// 定义页面类型，支持 getLayout
type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
  // 新增：页面可以指定需要在 Layout 外渲染的额外内容
  getExtraContent?: () => ReactNode;
  // 标记是否为 Landing/SEO 页面，这些页面不需要加载 Uploader 和神策 SDK
  isLandingPage?: boolean;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function mapLocale(locale: string): string {
  if (locale.startsWith('zh')) return 'zh';
  if (locale.startsWith('en')) return 'en';
  return 'en'; // 默认英文
}

if (typeof window === 'undefined') {
  React.useLayoutEffect = () => {};
}

function NextApp({
  Component,
  pageProps,
  locale: serverLocale,
}: AppPropsWithLayout & { locale: string }) {
  const { getUserBenefit, getFunctionLimitConfig } = UserStore.getState();
  const getUserInfo = useLoginStore.getState().getUserInfo;

  useTheme();

  // 初始化 Intercom 客服
  useInterCom();

  // 客户端重新检测语言，解决 SSG 页面构建时 locale 固定的问题
  const [locale, setLocale] = useState(serverLocale);

  useEffect(() => {
    // 客户端：使用浏览器语言覆盖构建时的默认值
    const browserLocale = mapLocale(window.navigator.language);
    if (browserLocale !== locale) {
      setLocale(browserLocale);
    }
  }, []);

  // 获取页面的额外内容（如 SEO 部分）
  const extraContent = Component.getExtraContent?.();

  // 使用 getLayout 模式，如果页面定义了 getLayout 就使用，否则默认使用 Layout 包裹
  const getLayout = Component.getLayout ?? ((page) => page);

  useEffect(() => {
    const favicon = document.getElementById('favicon') as HTMLLinkElement;
    // 检查系统主题
    const matchDark = window.matchMedia('(prefers-color-scheme: dark)');

    // 切换 favicon 的方法
    const updateFavicon = () => {
      if (matchDark.matches) {
        favicon.href = '/logo-black.ico'; // 暗黑模式图标
      } else {
        favicon.href = '/logo-light.ico'; // 亮色模式图标
      }
    };

    // 监听主题变化
    matchDark.addEventListener('change', updateFavicon);
    updateFavicon(); // 页面加载时初始化

    return () => matchDark.removeEventListener('change', updateFavicon);
  }, []);

  // 检查是否为 Landing/SEO 页面
  const isLandingPage = Component.isLandingPage === true;

  /**
   * 性能优化：延迟初始化 Uploader
   * Landing 页面完全跳过，功能页面延迟到浏览器空闲时初始化
   * 预期效果：减少 ~6MB (ali-oss SDK) 的首屏 JS 加载
   */
  useEffect(() => {
    if (isLandingPage) return;

    deferredInit(async () => {
      // 获取用户信息
      UserStore.getState().getUserBenefit();
      // 动态导入 Uploader，避免打包 COS/OSS SDK 到初始 bundle
      const { Uploader } = await import('@/utils/Uploader/BreakPointUploader');
      const instance = await Uploader.getInstance(false);
      instance.init();
      // 动态导入事件追踪器
      const { initTracker } = await import('@/utils/eventlog/logAdapter');
      initTracker();
    });
  }, [isLandingPage]);

  useEffect(() => {
    // 立即执行的关键初始化
    getFunctionLimitConfig();
    getUserInfo?.(25);
  }, []);

  const sdkInitRef = useRef(false);
  useEffect(() => {
    if (isLandingPage || sdkInitRef.current) return;
    sdkInitRef.current = true;

    /**
     * 性能优化：延迟初始化监控和分析 SDK
     * 这些 SDK 不影响用户可见内容和交互，延迟到浏览器空闲时加载
     * 预期效果：减少 TBT (Total Blocking Time)，提升 LCP
     */
    deferredInit(async () => {
      const firebaseAnalytics = (await import('@/utils/firebaseAnalytics'))
        .initFirebaseAnalytics;
      firebaseAnalytics();

      // 动态导入监控 SDK，避免打包到初始 bundle
      const initCloudCare = (await import('@/hooks/useCloudcare')).default;
      initCloudCare();

      // Landing 页面跳过神策 SDK 初始化
      const initShence = (await import('@/hooks/useShence')).default;
      initShence();

      // 动态导入在线时长追踪器
      const OnlineTimeTracker = (await import('@/utils/OnlineTimeTracker')).default;
      const tracker = OnlineTimeTracker.getInstance();
      tracker.init();

      // 开发环境输出构建信息
      if (process.env.NODE_ENV === 'development') {
        console.group('Build Info');
        console.log('\x1b[41;37m ⏳ Build Time \x1b[0m ' + buildTime);
        console.log('\x1b[44;37m 🌿 Branch Name \x1b[0m ' + branchName);
        console.log('\x1b[43;30m 🔗 Commit ID \x1b[0m ' + shortCommit);
        console.log('\x1b[45;37m 🗺️ App Area \x1b[0m ' + appArea);
        console.log('\x1b[46;30m 🌍 App Env \x1b[0m ' + appEnv);
        console.groupEnd();
      }
      // TODO 3.3后删除，规范cookie
      if (JSON.stringify(getCookie('userInfo')) !== '{}') {
        useLoginStore.getState().logout();
      }
    });
  }, [isLandingPage]);

  const router = useRouter();
  useEffect(() => {
    // 路由变更
    const handleRouteChange = () => {
      getUserBenefit();
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    // 清除事件监听器
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <GlobalAudioPlayerProvider>
      <RightProvider>
        <IntlProvider locale={locale} messages={locale === 'en' ? en : zh}>
          <ConfigProvider theme={theme}>
            <StyleProvider hashPriority="high">
              <PageHead />
              <ErrorBoundary>
                {/* <NextNProgress
                  color="#745CFF"
                  startPosition={0.3}
                  stopDelayMs={200}
                  height={3}
                  showOnShallow
                /> */}
                {getLayout(<Component {...pageProps} />)}
                {extraContent}
                <PlufginDector />
              </ErrorBoundary>
              <WithContext />
            </StyleProvider>
          </ConfigProvider>
        </IntlProvider>
      </RightProvider>
    </GlobalAudioPlayerProvider>
  );
}

NextApp.getInitialProps = async (
  context: AppContext,
): Promise<AppInitialProps & { locale: string; messages: Record<string, string> }> => {
  const ctx = context.ctx;

  let locale: string;
  if (ctx.req) {
    // 服务端：使用 HTTP header
    locale = mapLocale(ctx.req.headers['accept-language'] || 'en');
  } else {
    // 客户端：使用浏览器语言
    locale = typeof window !== 'undefined' ? mapLocale(window.navigator.language) : 'en';
  }

  // 获取原始的 pageProps
  const pageProps = (await context.Component.getInitialProps?.(ctx)) || {};

  try {
    const messages = await import(`../lang/${locale}.json`);
    return {
      pageProps,
      locale,
      messages: messages.default,
    };
  } catch (error) {
    console.error('Failed to load messages', error);
    return {
      pageProps,
      locale: 'en',
      messages: {},
    };
  }
};

export default NextApp;
